import { type ReactElement, isValidElement, useRef } from 'react';
import type { LayerHandle } from './useLayer';
import type { LayerCloseReason } from './types';
import { useUiT } from '../i18n';

/** Base UI change reasons → layer close reasons. */
export function closeReasonOf(baseReason: string): LayerCloseReason {
  switch (baseReason) {
    case 'escape-key':
    case 'close-watcher':
      return 'escape';
    case 'outside-press':
      return 'outside';
    case 'focus-out':
      return 'focus-out';
    case 'close-press':
      return 'close-button';
    case 'item-press':
      return 'select';
    case 'swipe':
      return 'swipe';
    default:
      return 'programmatic';
  }
}

type BaseDetails = {
  reason: string;
  event: Event;
  cancel: () => void;
  allowPropagation: () => void;
};

/**
 * Adapter for Base UI `onOpenChange(false, details)`: asks the stack whether the dismiss applies to
 * this layer. Returns the close reason, or null when the close was cancelled (another layer is on
 * top, or the dirty guard is asking).
 */
export function resolveDismiss(layer: LayerHandle, details: BaseDetails): LayerCloseReason | null {
  const reason = closeReasonOf(details.reason);
  const verdict = layer.dismiss(reason, details.event);
  if (verdict === 'close') return reason;
  details.cancel();
  // The event belongs to another layer: let it travel to the Escape router.
  if (verdict === 'ignore') details.allowPropagation();
  return null;
}

/** Whether a trigger element renders a native `<button>` (Base UI `nativeButton`). */
export function isNativeButtonElement(trigger: ReactElement, knownButtons: ReadonlySet<unknown>): boolean {
  if (!isValidElement(trigger)) return true;
  if (typeof trigger.type === 'string') return trigger.type === 'button';
  if (knownButtons.has(trigger.type)) return true;
  // Custom components: assume a button unless they say otherwise with `data-native-button="false"`.
  return (trigger.props as Record<string, unknown>)['data-native-button'] !== 'false';
}

export type Side = 'top' | 'bottom' | 'left' | 'right';
export type Align = 'start' | 'center' | 'end';

/** Floating UI placement strings (`bottom-start`) → Base UI side and align. */
export function sideAlign(
  placement: string | undefined,
  fallback = 'bottom-start',
): { side: Side; align: Align } {
  const [side, align] = (placement ?? fallback).split('-') as [Side, 'start' | 'end' | undefined];
  return { side, align: align ?? 'center' };
}

/**
 * Drag handle of a bottom sheet: drag down to dismiss (or tap the scrim, or Escape). Translates the
 * sheet while dragging; releases past 72 px or with a fast flick dismiss, otherwise it springs back.
 */
export function SheetGrabber({ onDismiss }: { onDismiss: () => void }) {
  const t = useUiT();
  const drag = useRef<{ y: number; t: number; sheet: HTMLElement; dy: number } | null>(null);
  return (
    <div
      className="sheet-grabber"
      data-base-ui-swipe-ignore=""
      aria-hidden="true"
      title={undefined}
      data-label={t('ui.sheet.drag')}
      onPointerDown={(e) => {
        const sheet = (e.currentTarget as HTMLElement).closest<HTMLElement>('[data-sheet]');
        if (!sheet) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        drag.current = { y: e.clientY, t: performance.now(), sheet, dy: 0 };
        sheet.style.transition = 'none';
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d) return;
        d.dy = Math.max(0, e.clientY - d.y);
        d.sheet.style.transform = `translateY(${d.dy}px)`;
      }}
      onPointerUp={() => {
        const d = drag.current;
        drag.current = null;
        if (!d) return;
        const velocity = d.dy / Math.max(1, performance.now() - d.t);
        d.sheet.style.transition = '';
        if (d.dy > 72 || velocity > 0.6) onDismiss();
        else d.sheet.style.transform = '';
      }}
      onPointerCancel={() => {
        const d = drag.current;
        drag.current = null;
        if (d) {
          d.sheet.style.transition = '';
          d.sheet.style.transform = '';
        }
      }}
    >
      <span />
    </div>
  );
}
